import { createContext, useContext, useEffect, useMemo, useState } from "react";

const CartContext = createContext(null);

export const itemKey = (i) => {
  const box = i.box_selection && i.box_selection.length ? `__box:${[...i.box_selection].sort().join("-")}` : "";
  const sub = i.subscription ? `__sub:${i.subscription.cadence}` : "";
  const gift = i.gift && i.gift.is_gift ? `__gift:${i.gift.recipient_name}-${i.gift.first_delivery_date}` : "";
  const base = i.slot_id ? `${i.product_id}__${i.slot_id}` : i.product_id;
  return `${base}${box}${sub}${gift}`;
};

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("abeille-cart")) || [];
    } catch {
      return [];
    }
  });
  const [giftWrap, setGiftWrap] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem("abeille-cart", JSON.stringify(items));
  }, [items]);

  const addItem = (product, quantity = 1, slot = null, boxSelection = null, meta = null) => {
    const newItem = {
      product_id: product.product_id,
      slot_id: slot?.slot_id || null,
      slot_label: slot ? `${slot.date} · ${slot.time}` : null,
      box_selection: boxSelection && boxSelection.length ? boxSelection : null,
      box_label: product.box_label || null,
      subscription: meta?.subscription || null,
      gift: meta?.gift || null,
      name: product.name,
      category: product.category || null,
      shipping_weight: product.shipping_weight ?? 250,
      price_cents: product.price_cents,
      image_url: product.image_url,
      weight: product.weight,
      quantity,
    };
    const key = itemKey(newItem);
    setItems((prev) => {
      const found = prev.find((i) => itemKey(i) === key);
      if (found) {
        return prev.map((i) =>
          itemKey(i) === key ? { ...i, quantity: Math.min(100, i.quantity + quantity) } : i
        );
      }
      return [...prev, newItem];
    });
  };

  const updateQuantity = (key, quantity) =>
    setItems((prev) =>
      quantity <= 0
        ? prev.filter((i) => itemKey(i) !== key)
        : prev.map((i) => (itemKey(i) === key ? { ...i, quantity } : i))
    );

  const removeItem = (key) => setItems((prev) => prev.filter((i) => itemKey(i) !== key));

  const clearCart = () => {
    setItems([]);
    setGiftWrap(false);
  };

  const count = items.reduce((sum, i) => sum + i.quantity, 0);
  const subtotal = items.reduce((sum, i) => sum + i.price_cents * i.quantity, 0);

  const value = useMemo(
    () => ({ items, addItem, updateQuantity, removeItem, clearCart, giftWrap, setGiftWrap, isOpen, setIsOpen, count, subtotal }),
    [items, giftWrap, isOpen, count, subtotal]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export const useCart = () => useContext(CartContext);
